// The alerts that follow Saudi Arabia's announcements, in the words the owner approved (BUILD-SPEC items 19 and 20),
// shared by the alerts Worker (server/alerts.ts, server/watch.ts) and the app's tests. Each says what Saudi Arabia
// said, or what its calendar gives; an alert about something still to come carries "in shāʾ Allah".

import { HIJRI_MONTHS } from '../astro/hijri';
import { addDays, clockAt, wallTime } from './localtime';

/**
 * - `call`: the Supreme Court calls for the crescent to be looked for (Ramaḍān, Shawwāl, Dhū al-Ḥijjah), with the
 *   evening of looking.
 * - `result`: Saudi Arabia has announced the month's 1st (`thirty` when the month before completes 30 days).
 * - `begins`: the new month begins, by Saudi Arabia's calendar (every other month, for those who chose it).
 */
export interface Notice {
  kind: 'call' | 'result' | 'begins';
  month: number;
  year: number;
  /** The Gregorian date of the 1st (result, begins). */
  start?: string;
  /** The Gregorian date of the evening of looking (call). */
  evening?: string;
  /** The month before completes 30 days (result). */
  thirty?: boolean;
}

/** A notice as an alert box sends it: the same shape as the reminders the app sets. */
export interface NoticeAlert { at: number; until: number; title: string; body: string; tag: string; url: string; asked: true }

const RIYADH = 'Asia/Riyadh';
const IA = 'in shāʾ Allah';
// Spelt out here rather than left to Intl, whose punctuation differs between runtimes ("Friday, 20 March").
const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
/** "Wednesday". */
export const weekday = (date: string) => WEEKDAYS[new Date(`${date}T12:00:00Z`).getUTCDay()];
/** "Friday 20 March". */
export const dayMonth = (date: string) => `${weekday(date)} ${Number(date.slice(8, 10))} ${MONTHS[Number(date.slice(5, 7)) - 1]}`;
/** "Friday 20 March 2026". */
export const longDate = (date: string) => `${dayMonth(date)} ${date.slice(0, 4)}`;

/** "tomorrow, Wednesday", or "today, Wednesday" where the 1st has already come (the far east, later), else "on Wednesday 18 February". */
function whenItBegins(start: string, tz: string, at: number): string {
  const today = clockAt(at, tz).date;
  if (start === addDays(today, 1)) return `tomorrow, ${weekday(start)}`;
  if (start === today) return `today, ${weekday(start)}`;
  return `on ${dayMonth(start)}`;
}

/** The words of a notice for someone at `tz`, reading it at `at`. */
export function noticeText(n: Notice, tz: string, at: number): { title: string; body: string } {
  const name = HIJRI_MONTHS[n.month - 1];
  const title = `${name} ${n.year}`;
  if (n.kind === 'call') return { title, body: `Tonight Saudi Arabia looks for the crescent of ${name}, ${IA}.` };
  const start = n.start!;
  if (n.kind === 'begins') return { title, body: `${name} begins ${whenItBegins(start, tz, at)}, ${IA}.` };
  if (n.thirty) {
    const before = HIJRI_MONTHS[(n.month + 10) % 12];
    return { title, body: `Saudi Arabia has announced: ${before} completes 30 days, so ${name} begins on ${dayMonth(start)}, ${IA}.` };
  }
  return { title, body: `Saudi Arabia has announced: ${name} begins ${whenItBegins(start, tz, at)}, ${IA}.` };
}

/** Within waking hours (08:00 to 21:00 on the local clock): the time itself, or the evening before it, or the next morning. */
function waking(want: number, now: number, tz: string): number {
  const at = Math.max(want, now);
  const c = clockAt(at, tz);
  if (c.hour >= 8 && c.hour < 21) return at;
  if (c.hour < 8) return wallTime(c.date, tz, 8);
  const evening = wallTime(c.date, tz, 20) + 30 * 60e3;
  return evening > now ? evening : wallTime(addDays(c.date, 1), tz, 8);
}

/**
 * When and how a notice reaches someone at `tz`, received at `now`; null when it would come too late to be of use.
 * - A result goes at once, whatever the hour: whether tomorrow is the 1st matters tonight.
 * - The call goes at about midday in Riyadh on the evening of looking, moved into the reader's waking hours (in the
 *   far west, the morning of that day; in the far east, its evening), and no later than the small hours in Riyadh.
 * - "Begins" goes in waking hours: about midday in Riyadh on the day before, or as soon as it is announced.
 */
export function noticeAlert(n: Notice, tz: string, now: number): NoticeAlert | null {
  const tag = `saudi-${n.year}-${String(n.month).padStart(2, '0')}`;
  // The 1st is no news once its day is over in Riyadh.
  const endOfFirst = n.start ? wallTime(addDays(n.start, 1), RIYADH, 0) : 0;
  let at: number, until: number;
  if (n.kind === 'result') {
    at = now;
    until = endOfFirst;
  } else if (n.kind === 'call') {
    at = waking(wallTime(n.evening!, RIYADH, 12), now, tz);
    until = wallTime(addDays(n.evening!, 1), RIYADH, 3);
  } else {
    const before = wallTime(addDays(n.start!, -1), RIYADH, 12);
    at = waking(before, now, tz);
    until = endOfFirst;
  }
  if (at >= until) return null;
  return { at, until, ...noticeText(n, tz, at), tag, url: '/?panel=tonight', asked: true };
}
